import React from "react"
import { Box, Checkbox, Divider, IconButton, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { IconSeeker } from "@ui-seeker/core"

import {
	AppAntSwitch, AppButtonIcon, AppCardTitle, AppOfficesBlock, AppRetailerBlockContainer, AppRunProgress, AppSelectionBar, AppSelectionBarButton,
	AppSelectionBarDivider, AppSelectionBarMenu, AppStatusFilterToggle, AppToolbarCreateButton,
} from "@components"
import AppOfficesBlockContainer from "@components/DataBlocks/OfficesContainer"
import ReportIconsLegend from "Pages/ReportsAlerts/Reports/@components/IconsLegend"
import NumSearchesBlock from "Pages/ReportsAlerts/Reports/@components/NumSearchesBlock"
import { dsColorNew, shades } from "@palette"


const noop = () => {}

// alignments as the real columns set them (the table centres a head without headerAlign)
export const REPORTS_DEMO_HEADERS = [
	{ label: 'Report name' },
	{ label: 'Search numbers', align: 'center' },
	{ label: 'Sites' },
	{ label: 'Frequency Hours', align: 'center' },
	{ label: 'Frequency', align: 'center' },
	{ label: 'Pick up & Drop off' },
	{ label: 'Date', align: 'center' },
	{ label: 'Off/On', align: 'center' },
	{ label: 'Actions', align: 'center' },
]

// The reports table as the Virtual Tour shows it when the account has no reports: same card, toolbar,
// header, rows and footer as the real one, with nothing that acts
export default function ReportsDemoTable({ title, headers = REPORTS_DEMO_HEADERS, count, selection = null, children }) {
	// the real table's Paper (MUIDataTable paper: white, 16px padding, theme radius), so the tour finds the card from its title
	return <Paper elevation={0} sx={{ backgroundColor: dsColorNew.white, p: 2 }}>
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pl: 0.5, pb: 1.5 }}>
			<AppCardTitle icon="file-lines" title={title}>
				<Box component="span" sx={{ ml: 0.75 }}>
					<AppStatusFilterToggle aria-label="Reports shown" data-tour-id="tour-status-toggle" filter={['Enabled']} onChange={noop}/>
				</Box>
			</AppCardTitle>
			<Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5, color: dsColorNew.textMuted }}>
				{['magnifying-glass', 'download', 'filter'].map(icon => <IconButton key={icon} tabIndex={-1} sx={{ borderRadius: '8px' }}>
					<IconSeeker icon={icon} sx={{ fontSize: 16 }}/>
				</IconButton>)}
				<Divider orientation="vertical" flexItem sx={{ my: 1, ml: 1, mr: 1.5 }}/>
				<AppToolbarCreateButton data-tour-id="tour-create-report" divider={false} label="Create Report" icon="file-plus" tabIndex={-1} onClick={noop}/>
			</Box>
		</Box>

		<Box sx={{ overflowX: 'auto' }}>
			<Table sx={{
				// the head itself comes from the theme (MuiTableCell head); only its height and one-line labels here
				'& th': { py: 1.5, whiteSpace: 'nowrap' },
				'& td': { fontSize: 13, color: dsColorNew.textDark, py: 2, borderBottom: `1px solid ${dsColorNew.grayBorder}` },
				// the real cells' Typography variants at the data tables' size (MUIDataTableBodyCell)
				'& td .MuiTypography-body1, & td .MuiTypography-body2, & td .MuiTypography-subtitle2': { fontSize: 13 },
				'& td .MuiTypography-subtitle1': { fontSize: 14 },
				'& td .MuiTypography-caption': { fontSize: 12 },
				'& tbody tr:nth-of-type(even)': { backgroundColor: dsColorNew.tableRowAlt },
				'& tbody tr.is-selected': { backgroundColor: dsColorNew.pillActiveBg, boxShadow: `inset 3px 0 0 ${dsColorNew.appBlue}` },
			}}>
				<TableHead>
					<TableRow>
						<TableCell padding="checkbox"><Checkbox size="small" tabIndex={-1} sx={{ color: alpha(dsColorNew.white, 0.7) }}/></TableCell>
						{headers.map(({ label, align }) => <TableCell key={label} align={align}>{label}</TableCell>)}
					</TableRow>
				</TableHead>
				<TableBody>{children}</TableBody>
			</Table>
		</Box>

		<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pt: 1.5, pl: 1 }}>
			<ReportIconsLegend variant="plain" sx={{ flexShrink: 1 }}/>
			<Typography sx={{ ml: 'auto', pr: 2, fontSize: 14, color: dsColorNew.textDark, whiteSpace: 'nowrap' }}>1-{count} of {count}</Typography>
		</Box>

		{selection}
	</Paper>
}

// `onToggle` makes the row's box tickable (the tour ticks the first one to bring up the selection bar)
export function ReportsDemoRow({ selected = false, onToggle = noop, children }) {
	return <TableRow className={selected ? 'is-selected' : undefined}>
		<TableCell padding="checkbox"><Checkbox size="small" checked={selected} onChange={onToggle} tabIndex={-1} sx={{ color: dsColorNew.webBlue500, '&.Mui-checked': { color: dsColorNew.webBlue500 } }}/></TableCell>
		{children}
	</TableRow>
}

// The quick links under a finished execution's date, in AppLinksBlock's order and colours
const DEMO_LINKS = {
	lor: { icon: 'tally', title: 'LOR Analyzer', tourId: 'tour-date-btn-lor' },
	dor: { icon: 'calendar-days', title: 'DOR Analyzer', color: dsColorNew.appBlue, tourId: 'tour-date-btn-dor' },
	direct: { icon: 'chart-line', title: 'Direct Site Analyzer', color: dsColorNew.successText, tourId: 'tour-date-btn-direct' },
	brokers: { icon: 'handshake', title: 'Brokers Analyzer', color: dsColorNew.noteAmber, tourId: 'tour-date-btn-brokers' },
	rating: { icon: 'star', title: 'Rating Analyzer', color: dsColorNew.noteAmber, tourId: 'tour-date-btn-rating' },
	realtime: { icon: 'clock-rotate-left', title: 'View in real time historical', tourId: 'tour-date-btn-realtime' },
	categories: { icon: 'file-excel', title: 'Get custom categories matches', color: shades.deepOrange[400], tourId: 'tour-date-btn-categories' },
	acriss: { icon: 'file-excel', title: 'Get custom acriss matches', color: shades.teal[400], tourId: 'tour-date-btn-acriss' },
	report: { icon: 'cloud-arrow-down', title: 'Download report', color: dsColorNew.successText, tourId: 'tour-date-btn-report' },
	export: { icon: 'file-arrow-down', title: 'Download export', color: dsColorNew.appBlue, tourId: 'tour-date-btn-export' },
	multireport: { icon: 'cloud-arrow-down', title: 'Download multi report', color: shades.pink[400], tourId: 'tour-date-btn-multireport' },
	multiexport: { icon: 'file-arrow-down', title: 'Download multi export', color: shades.deepPurple[400], tourId: 'tour-date-btn-multiexport' },
}
const DEMO_LINKS_ORDER = Object.keys(DEMO_LINKS)

function ReportsDemoLinks({ links = [] }) {
	return <Box sx={{ display: 'flex', gap: .5 }}>
		{DEMO_LINKS_ORDER.filter(key => links.includes(key)).map(key => {
			const { icon, title, color, tourId } = DEMO_LINKS[key]
			return <AppButtonIcon key={key} icon={icon} title={title} data-tour-id={tourId} tabIndex={-1} sx={color ? { color } : {}}/>
		})}
	</Box>
}

// One report's cells as the real columns draw them; `links` are the quick links of a finished execution
export function ReportsDemoCells({ report: r, links = [], withForce = false }) {
	const isRunning = r.status === 'running'

	return <>
		<TableCell>
			<Typography variant="subtitle1">{r.name}</Typography>
		</TableCell>

		<TableCell align="center">
			<Box sx={{ display: 'flex', justifyContent: 'center' }}>
				<NumSearchesBlock numSearches={r.searches}/>
			</Box>
		</TableCell>

		<TableCell>
			<AppRetailerBlockContainer rows={r.sites.map(name => ({ name }))}/>
		</TableCell>

		<TableCell>
			<Typography sx={{ textAlign: 'center' }}>{r.hours.join(', ')}</Typography>
		</TableCell>

		<TableCell>
			<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
				{r.frequency.map(f => <Typography key={f}>{f}</Typography>)}
			</Box>
		</TableCell>

		<TableCell>
			<Box sx={{ textWrap: 'nowrap', display: 'flex', alignItems: 'center', justifyContent: 'start', gap: 2 }}>
				<AppOfficesBlockContainer rows={[{ name: r.pickup, type: 'airport' }]}/>
				{!!r.dropoff && <>
					<Typography sx={{ fontSize: 15, fontWeight: 'bold' }}>/</Typography>
					<AppOfficesBlock name={r.dropoff} type="airport"/>
				</>}
			</Box>
		</TableCell>

		<TableCell align="center" sx={{ minWidth: 100 }}>
			<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 90 }}>
				{!r.date && !isRunning && <Typography variant="caption">Not executed</Typography>}
				{r.status === 'ready' && <>
					<Typography variant="caption" sx={{ mb: 1 }}>{r.date}</Typography>
					<ReportsDemoLinks links={links}/>
				</>}
				{isRunning && <AppRunProgress data-tour-id="tour-running-block" label="Running" actual={r.progress} total={100} done={r.completeTasks} totalText={r.totalTasks}/>}
				{r.status === 'stopped' && <>
					<Typography variant="caption">{r.date}</Typography>
					<Typography variant="body1">
						<IconSeeker icon="hand" sx={{ color: dsColorNew.dangerRed }}/> Stopped
					</Typography>
				</>}
			</Box>
		</TableCell>

		<TableCell align="center">
			<Box sx={{ display: 'flex', justifyContent: 'center' }}>
				<AppAntSwitch checked={r.enabled} onChange={noop}/>
			</Box>
		</TableCell>

		<TableCell align="center">
			<Box sx={{ display: 'flex', justifyContent: 'center' }}>
				{/* as the real cell: Perform only for an enabled report, and Perform / Edit / Clone / Delete hidden while it runs */}
				{!isRunning && r.enabled && <AppButtonIcon icon="gears" title="Perform" tabIndex={-1}/>}
				{!isRunning && <AppButtonIcon icon="pen" title="Edit" tabIndex={-1}/>}
				{!isRunning && <AppButtonIcon icon="clone" title="Clone & Edit" tabIndex={-1}/>}
				<AppButtonIcon icon="circle-info" title="Show Report Info" data-tour-id="tour-show-report-info" tabIndex={-1}/>
				{withForce && isRunning && <Box data-tour-id="tour-force-action">
					<AppButtonIcon icon="circle-stop" title="Stop Execution" tabIndex={-1}/>
				</Box>}
				{!isRunning && <AppButtonIcon icon="trash-can" title="Delete" tabIndex={-1}/>}
			</Box>
		</TableCell>
	</>
}

// The floating selection bar of the real table for the ticked demo `reports`; every button the tour points at,
// with Perform and Stop under the real bar's conditions
export function ReportsDemoSelectionBar({ reports = [], onClear = noop }) {
	const allEnabled = reports.every(r => r.enabled)
	const allRunning = reports.every(r => r.status === 'running' && r.enabled)

	return <AppSelectionBar data-tour-id="tour-multi-bar" count={reports.length} onClear={onClear}>
		<AppSelectionBarMenu data-tour-id="tour-multi-btn-edit" icon="pen-to-square" label="Edit" items={[
			{ 'data-tour-id': 'tour-multi-btn-sites', icon: 'globe-pointer', label: 'Sites' },
			{ 'data-tour-id': 'tour-multi-btn-pickups', icon: 'calendars', label: 'Pick-up dates', hint: 'specific days only' },
			{ 'data-tour-id': 'tour-multi-btn-emails', icon: 'envelopes', label: 'Emails' },
			{ 'data-tour-id': 'tour-multi-btn-frequency', icon: 'calendar-clock', label: 'Frequency' },
			{ 'data-tour-id': 'tour-multi-btn-execution-dates', icon: 'calendar-lines', label: 'Execution date range' },
		]}/>
		{allEnabled && <AppSelectionBarButton data-tour-id="tour-multi-btn-perform" icon="gears" label="Perform"/>}
		{allRunning && <AppSelectionBarButton data-tour-id="tour-multi-btn-stop" icon="circle-stop" label="Stop"/>}
		<AppSelectionBarButton data-tour-id="tour-multi-btn-copy" icon="copy" label="Copy"/>
		<AppSelectionBarButton data-tour-id="tour-multi-btn-enable" icon="toggle-on" label="Enable"/>
		<AppSelectionBarButton data-tour-id="tour-multi-btn-disable" icon="toggle-off" label="Disable"/>
		<AppSelectionBarDivider/>
		<AppSelectionBarButton data-tour-id="tour-multi-btn-delete" icon="trash" label="Delete" danger/>
	</AppSelectionBar>
}
